// The colour theme: System (follows the device), Light or Dark, chosen per browser.
//
// System needs no script at all: the CSS follows prefers-color-scheme (index.css). Light or Dark
// sets <html data-theme>, which public/theme.js applies from storage before the first paint (a
// classic script in <head>, as the CSP allows no inline script); this module changes it later.

import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/** localStorage key, holding "light" or "dark" (no key: System). public/theme.js reads it too. */
export const THEME_STORAGE_KEY = 'sk.theme';

type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function localStore(): ThemeStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The saved choice: System when nothing valid is saved or storage is blocked. */
export function readThemeChoice(storage: ThemeStorage | null = localStore()): ThemeChoice {
  try {
    const saved = storage?.getItem(THEME_STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
}

/** Saves the choice (System removes the key); false when storage is blocked or full. */
export function writeThemeChoice(choice: ThemeChoice, storage: ThemeStorage | null = localStore()): boolean {
  try {
    if (!storage) return false;
    if (choice === 'system') storage.removeItem(THEME_STORAGE_KEY);
    else storage.setItem(THEME_STORAGE_KEY, choice);
    return true;
  } catch {
    return false;
  }
}

/** The theme shown for a choice: System follows the device. */
export function resolveTheme(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice !== 'system') return choice;
  return systemDark ? 'dark' : 'light';
}

/**
 * Shows a choice: <html data-theme="light|dark">, or no attribute for System (the media query
 * decides), and the color-scheme meta tag that colours the page before the stylesheet arrives.
 */
export function applyThemeChoice(choice: ThemeChoice, doc: Document = document): void {
  const root = doc.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  doc.querySelector('meta[name="color-scheme"]')?.setAttribute('content', choice === 'system' ? 'light dark' : choice);
}

// --- Shared state for the switches (all tabs follow a change made in one) ---

let current: ThemeChoice | null = null;
const listeners = new Set<() => void>();

function getChoice(): ThemeChoice {
  current ??= readThemeChoice();
  return current;
}

function onStorage(e: StorageEvent): void {
  if (e.key !== THEME_STORAGE_KEY && e.key !== null) return;
  const next = readThemeChoice();
  if (next === current) return;
  current = next;
  applyThemeChoice(next);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Applies and saves a choice; it still applies in this tab when storage is blocked. */
export function setThemeChoice(choice: ThemeChoice): void {
  current = choice;
  writeThemeChoice(choice);
  applyThemeChoice(choice);
  listeners.forEach((l) => l());
}

/** At startup: re-applies the saved choice (in case public/theme.js did not run) and follows other tabs. */
export function initTheme(): void {
  applyThemeChoice(getChoice());
  window.addEventListener('storage', onStorage);
}

/** The current choice; System on the server (the switch then renders with System selected). */
export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(subscribe, getChoice, () => 'system');
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function subscribeDevice(listener: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}

/** The theme System shows on this device now (light on the server). */
export function useSystemTheme(): Theme {
  const dark = useSyncExternalStore(
    subscribeDevice,
    () => window.matchMedia(DARK_QUERY).matches,
    () => false,
  );
  return resolveTheme('system', dark);
}
